import Filter from 'models/api/filter';
import {
    WorkplaceFilterType,
    WorkplaceFilterTypeItem,
    WorkplaceFilterValue,
} from 'providers/GridFilterUrlProvider';
import { SetStateAction, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseToInt } from 'utils/number';
import useUpdateEffect from './useUpdateEffect';

export default function useGridUrlParams<FilterType extends Filter>(
    initialFilterState: FilterType,
    defaultFilterParams: WorkplaceFilterType<FilterType>,
    urlFilterKeyPrefix?: string,
    fixUrlFilter?: (filter: FilterType) => FilterType
) {
    const parseUrl = (searchParams: URLSearchParams) => {
        const params: {
            [key: string]: WorkplaceFilterValue;
        } = {};
        for (const [paramKey, value] of searchParams.entries()) {
            if (urlFilterKeyPrefix) {
                const prefix = paramKey.split('.')[0];
                if (prefix !== urlFilterKeyPrefix) {
                    continue;
                }
            }
            const name = urlFilterKeyPrefix ? paramKey.split('.')[1] : paramKey;
            const filterItem = defaultFilterParams[name] as WorkplaceFilterTypeItem;

            if (filterItem && filterItem instanceof Object) {
                if ('parse' in filterItem && filterItem.parse) {
                    params[name] = filterItem.parse(value);
                }
                if ('array' in filterItem && filterItem.array) {
                    const prop = params[name];
                    if (Array.isArray(prop)) {
                        prop.push(value as never);
                    } else {
                        params[name] = [value];
                    }
                } else {
                    params[name] = value;
                }
            } else {
                if (typeof initialFilterState[name] === 'number') {
                    params[name] = parseToInt(value);
                } else if (typeof initialFilterState[name] === 'boolean') {
                    params[name] = value.toLowerCase() === true.toString();
                } else {
                    params[name] = value;
                }
            }
        }

        return { ...(params as FilterType) };
    };

    const isEmpty = (value: any) => {
        return value == null || value === '' || (Array.isArray(value) && !value.length);
    };

    const initialFilter: FilterType = useMemo(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const filter = parseUrl(searchParams);
        if (!Object.keys(filter).length) {
            const result = {} as any;
            for (const key in initialFilterState) {
                const value = initialFilterState[key];
                if (!isEmpty(value)) {
                    result[key] = value;
                }
            }

            return result;
        } else {
            return filter;
        }
    }, []);

    const [searchParams, setSearchParams] = useSearchParams();
    const [filter, setFilter] = useState<FilterType>(initialFilter);

    useUpdateEffect(() => {
        const newFilter = parseUrl(searchParams);
        try {
            if (JSON.stringify(newFilter) !== JSON.stringify(filter)) {
                setFilter(newFilter);
            }
        } catch (_) {
            setFilter(newFilter);
        }
    }, [searchParams]);

    const updateUrl = useCallback((filter: FilterType, initial?: boolean) => {
        filter = fixUrlFilter ? fixUrlFilter(filter) : filter;
        const newUrlParams = new URLSearchParams();
        for (const name in filter) {
            const newValue = filter[name];
            const defaultItem = defaultFilterParams[name] as WorkplaceFilterTypeItem;
            if (
                !(
                    defaultItem instanceof Object &&
                    'urlIgnore' in defaultItem &&
                    defaultItem.urlIgnore
                ) &&
                !isEmpty(newValue)
            ) {
                const k = urlFilterKeyPrefix ? `${urlFilterKeyPrefix}.${name}` : name;

                if (Array.isArray(newValue)) {
                    newValue.forEach((v: any) => newUrlParams.append(k, v.toString()));
                } else {
                    newUrlParams.set(k, newValue);
                }
            }
        }
        const searchParams = new URLSearchParams(window.location.search);
        for (const [key, value] of searchParams.entries()) {
            const prefix = key.indexOf('.') > -1 ? key.split('.')[0] : undefined;

            if (prefix === urlFilterKeyPrefix) {
                continue;
            }

            newUrlParams.set(key, value);
        }

        setSearchParams(newUrlParams, { replace: initial });

        if (!initial) {
            setFilter(parseUrl(newUrlParams));
        }
    }, []);

    const result: [FilterType, (param: SetStateAction<FilterType>) => void] = [
        filter,
        (param) => {
            param instanceof Function
                ? setFilter((f) => {
                      const newVal = param(f);
                      updateUrl(newVal);
                      return f;
                  })
                : updateUrl(param);
        },
    ];
    return result;
}
